/**
 * String formatting utilities for labels and values
 */

/**
 * Formats enum values from UPPER_CASE to Title Case
 * @example formatEnumLabel('DAILY_STEPS') // 'Daily Steps'
 */
export const formatEnumLabel = (value: string): string =>
    value
        .toLowerCase()
        .split('_')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');

/**
 * Formats property keys from camelCase or snake_case to Title Case
 * @example formatKeyLabel('activityPoints') // 'Activity Points'
 * @example formatKeyLabel('user_id') // 'User ID'
 */
export const formatKeyLabel = (value: string): string => {
    const withSpaces = value
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/[_-]+/g, ' ')
        .trim();
    const tokens = withSpaces.split(' ').filter(Boolean);
    const upperTokens = new Set(['id', 'url', 'gpx', 'gps', 'v2']);

    return tokens
        .map((token) => {
            const lower = token.toLowerCase();
            if (upperTokens.has(lower)) {
                return lower.toUpperCase();
            }
            return lower.charAt(0).toUpperCase() + lower.slice(1);
        })
        .join(' ');
};

/**
 * Formats metric keys with domain-specific overrides
 * @example formatMetricLabel('averageSpeed') // 'Avg. Speed'
 */
export const formatMetricLabel = (key: string): string => {
    if (key.startsWith('calculatedMetric.')) {
        return formatKeyLabel(key.replace('calculatedMetric.', ''));
    }
    const normalized = key.toLowerCase();
    const overrides: Array<{ tokens: string[]; label: string }> = [
        {
            tokens: ['maxspeed', 'maximumspeed', 'max_speed'],
            label: 'Max Speed'
        },
        {
            tokens: ['averagespeed', 'avg_speed', 'avg speed', 'average speed'],
            label: 'Avg. Speed'
        },
        { tokens: ['distance'], label: 'Distance' },
        { tokens: ['duration', 'activitytime', 'time'], label: 'Duration' },
        { tokens: ['heartrate', 'heart'], label: 'Heart Rate' },
        { tokens: ['pace'], label: 'Avg. Pace' },
        { tokens: ['speed'], label: 'Speed' },
        { tokens: ['calories', 'burned'], label: 'Calories' },
        { tokens: ['elevation'], label: 'Elevation' },
        { tokens: ['steps', 'stepcount'], label: 'Steps' },
        { tokens: ['vo2'], label: 'VO2 Max' },
        { tokens: ['workouttype'], label: 'Workout Type' },
        { tokens: ['workoutcategory'], label: 'Workout Category' },
        { tokens: ['facility'], label: 'Facility' }
    ];

    const override = overrides.find(({ tokens }) =>
        tokens.some((token) => normalized.includes(token))
    );

    return override?.label ?? formatKeyLabel(key);
};

/**
 * Checks if a string is in UPPER_CASE enum format
 */
export const isEnumString = (value: string): boolean =>
    /^[A-Z0-9_]+$/.test(value);

/**
 * Formats property values for display (handles arrays, enums, primitives)
 */
export const formatPropertyValue = (value: unknown): string => {
    if (value === null || value === undefined) {
        return '—';
    }

    if (Array.isArray(value)) {
        const flat = value.flatMap((item) =>
            typeof item === 'string'
                ? isEnumString(item)
                    ? formatEnumLabel(item)
                    : item
                : typeof item === 'number' || typeof item === 'boolean'
                  ? String(item)
                  : JSON.stringify(item)
        );
        return flat.join(', ');
    }

    if (typeof value === 'string') {
        return isEnumString(value) ? formatEnumLabel(value) : value;
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
    }

    return JSON.stringify(value);
};

/**
 * Formats a number with specified decimal places
 * Removes trailing zeros after decimal point
 */
export const formatNumber = (value: number, decimals: number): string => {
    const formatted = value.toFixed(decimals);
    return decimals > 0 ? formatted.replace(/\.0+$/, '') : formatted;
};

/**
 * Formats a number with locale-aware rounding
 */
export const formatRounded = (value: number, decimals: number): string =>
    value.toLocaleString(undefined, {
        maximumFractionDigits: decimals,
        minimumFractionDigits: 0
    });
